import { useTranslation } from "react-i18next";
import Button from "@/components/ui/Buttons/Button/Button.tsx";
import IconDelete from "@/assets/icons/delete.svg?react";
import IconChevronLeft from "@/assets/icons/chevron-left.svg?react";
import ImagePlaceHolder from "@/assets/icons/placeholder.svg?react";
import LoaderIcon from "@/assets/icons/loader.svg?react";
import useConfig from "@/features/config/hooks/useConfig.tsx";
import uiHelper from "@/utils/uiHelper.tsx";
import { comparisonStore } from "@/features/widgets/components/Comparison/utils/comparisonStore.tsx";
import { Product } from "@/features/widgets/components/Comparison/types/ComparisonTypes";
import "./styles/SidePanel.scss";

const CLASS_PREFIX = `ws-widgets-comparison`;

type ComparisonSidePanelProps = {
  products: Product[];
  sidePanelOpened: boolean;
  setSidePanelOpened: (opened: boolean) => void;
  sidePanelPeeks: boolean;
  error: string | null;
  fetchTriggered: boolean;
  comparisonLoading: boolean;
  onCompare: () => void;
}

export const ComparisonSidePanel = ({
  products,
  sidePanelOpened,
  setSidePanelOpened,
  sidePanelPeeks,
  error,
  fetchTriggered,
  comparisonLoading,
  onCompare,
}: ComparisonSidePanelProps) => {
  const { t } = useTranslation();
  const config = useConfig();
  const sidePanelVisible = products.length >= 1;

  const classes = [
    `${ CLASS_PREFIX }-sidepanel`,
    `${ CLASS_PREFIX }-sidepanel--${ sidePanelOpened ? `opened` : `closed` }`,
    `${ CLASS_PREFIX }-sidepanel--${ sidePanelVisible ? `visible` : `hidden` }`,
    sidePanelPeeks && !sidePanelOpened ? `${ CLASS_PREFIX }-sidepanel--peeking` : ``,
  ];

  const toggleButtonClasses = [
    `${ CLASS_PREFIX }-sidepanel__handle`,
    `${ CLASS_PREFIX }-sidepanel__handle--${ sidePanelOpened ? `close` : `open` }`,
    `${ CLASS_PREFIX }-sidepanel-handle`,
    `${ CLASS_PREFIX }-sidepanel-handle--${ sidePanelOpened ? `close` : `open` }`,
  ];

  const toggleButtonIconClasses = [
    `${ CLASS_PREFIX }-sidepanel-handle__icon`,
    `${ CLASS_PREFIX }-sidepanel-handle__icon--${ sidePanelOpened ? `close` : `open` }`,
  ];

  const compareButtonClasses = [
    `${ CLASS_PREFIX }-sidepanel__submit`,
    error ? `${ CLASS_PREFIX }-sidepanel__submit--disabled` : ``,
    comparisonLoading ? `${ CLASS_PREFIX }-sidepanel__submit--loading` : ``,
  ];

  return (
    <>
      <div
        className={ [
          `${ CLASS_PREFIX }-sidepanel-backdrop`,
          sidePanelOpened && sidePanelVisible ? `${ CLASS_PREFIX }-sidepanel-backdrop--visible` : ``,
        ].join(` `) }
        onClick={ () => setSidePanelOpened(false) }
        aria-hidden="true"
      />
      <aside
        className={ classes.join(` `) }
        style={ { visibility: sidePanelVisible ? `visible` : `hidden` } }
        aria-live="polite"
        aria-atomic="false"
      >
        <button
          aria-expanded={ sidePanelOpened }
          className={ toggleButtonClasses.join(` `) }
          onClick={ () => setSidePanelOpened(!sidePanelOpened) }
        >
          <span className={ toggleButtonIconClasses.join(` `) }>
            <IconChevronLeft/>
          </span>
          <span className={ `${ CLASS_PREFIX }-sidepanel-handle__text` }>
            { t(`comparison.sidepanel.title`) }
          </span>
          <span className={ `${ CLASS_PREFIX }-sidepanel-handle__count` }>
            { products.length }
          </span>
          <span className={ `visually-hidden` }>
            { sidePanelOpened ? t(`core.button.close`) : t(`core.button.open`) }
          </span>
        </button>
        <div className={ `${ CLASS_PREFIX }-sidepanel__content` }>
          <ul className={ `${ CLASS_PREFIX }-sidepanel__products` }>
            {
              products.map((product, index) => (
                <li key={ index }
                    className={ `${ CLASS_PREFIX }-sidepanel__product ${ CLASS_PREFIX }-sidepanel-product` }>
                  <a className={ `${ CLASS_PREFIX }-sidepanel-product__link` } href={ product.url }>
                    <div className={ `${ CLASS_PREFIX }-sidepanel-product__image` }>
                      { product.image ?
                        <img src={ product.image } alt={ product.name }/>
                        :
                        <ImagePlaceHolder/>
                      }
                    </div>
                    <div className={ `${ CLASS_PREFIX }-sidepanel-product__content` }>
                      <div className={ `${ CLASS_PREFIX }-sidepanel-product__title` }>
                        { uiHelper.shorten(product.name, 40) }
                      </div>
                      { product.price &&
                        <div className={ `${ CLASS_PREFIX }-sidepanel-product__price` }>
                          { uiHelper.formatPrice(product.price, config.language, config.currency) }
                        </div>
                      }
                    </div>
                  </a>
                  <Button
                    className={ `${ CLASS_PREFIX }-sidepanel-product__delete` }
                    onClick={
                      () => {
                        comparisonStore.remove(product.url);
                      }
                    }
                  >
                    <span
                      className={ `${ CLASS_PREFIX }-sidepanel-product__delete-text` }>{ t(`core.button.remove`) }</span>
                    <span className={ `${ CLASS_PREFIX }-sidepanel-product__delete-icon` }><IconDelete/></span>
                  </Button>
                </li>
              ))
            }
          </ul>
          { error &&
            <div className={ `${ CLASS_PREFIX }-sidepanel__error` } aria-live="polite">
              <p>{ error }</p>
            </div>
          }
          <div className={ `${ CLASS_PREFIX }-sidepanel__buttons` }>
            <Button
              className={ compareButtonClasses.join(` `) }
              variant={ `primary` }
              onClick={ () => onCompare() }
              disabled={ error ? true : fetchTriggered }
            >
              { fetchTriggered
                ? <LoaderIcon />
                : t(`core.button.compare`)
              }
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
}

export default ComparisonSidePanel;
